/**
 * Reading and writing the CSS `box-shadow` strings the playEngine stores.
 *
 * `boxShadow` is a raw CSS string on the engine side (Image, SolidColor,
 * MaskedContainer, CarouselBundle, …) and used to be edited as one: nudging a
 * shadow down two pixels meant retyping "0 6px 18px rgba(0,0,0,0.10)" and
 * reading the result off the preview. These turn that string into the numbers a
 * person actually thinks in, and back.
 *
 * The parser is deliberately strict. Anything it cannot represent EXACTLY — two
 * shadows, a named colour, `em` offsets, a CSS variable — comes back null, and
 * the field falls back to the plain text box. A structured editor that silently
 * rewrites a value it did not fully understand is worse than no structured
 * editor at all.
 */

/** What "add a shadow" starts from: visible, soft, and obviously below. */
export const DEFAULT_BOX_SHADOW = {
  x: 0,
  y: 4,
  blur: 12,
  spread: 0,
  color: "#000000",
  alpha: 0.25,
  inset: false,
};

const BOX_SHADOW_LENGTH_RE = /^-?(?:\d+\.?\d*|\.\d+)(px)?$/;

/** Split on whitespace that is not inside brackets, so `rgba(0, 0, 0, .5)` survives. */
function splitShadowTokens(value) {
  const tokens = [];
  let current = "";
  let depth = 0;

  for (const char of value) {
    if (char === "(") depth += 1;
    if (char === ")") depth = Math.max(0, depth - 1);
    if (/\s/.test(char) && depth === 0) {
      if (current) tokens.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  if (current) tokens.push(current);
  return tokens;
}

/** `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb()` and `rgba()` → `{ color, alpha }`. */
function parseShadowColor(token) {
  const hex = /^#([0-9a-f]{3,8})$/i.exec(token);
  if (hex) {
    const digits = hex[1];
    if (digits.length === 3) {
      const [r, g, b] = digits.toLowerCase();
      return { color: `#${r}${r}${g}${g}${b}${b}`, alpha: 1 };
    }
    if (digits.length === 6) return { color: `#${digits.toLowerCase()}`, alpha: 1 };
    if (digits.length === 8) {
      return {
        color: `#${digits.slice(0, 6).toLowerCase()}`,
        alpha: Math.round((parseInt(digits.slice(6), 16) / 255) * 100) / 100,
      };
    }
    return null;
  }

  const rgb = /^rgba?\(([^)]*)\)$/i.exec(token);
  if (!rgb) return null;

  const parts = rgb[1].split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return null;

  const channels = parts.slice(0, 3).map((part) => Number(part));
  if (channels.some((channel) => !isFinite(channel) || channel < 0 || channel > 255)) return null;

  let alpha = 1;
  if (parts.length === 4) {
    const raw = parts[3];
    alpha = raw.endsWith("%") ? Number(raw.slice(0, -1)) / 100 : Number(raw);
    if (!isFinite(alpha) || alpha < 0 || alpha > 1) return null;
  }

  const hexOf = (channel) => Math.round(channel).toString(16).padStart(2, "0");
  return { color: `#${channels.map(hexOf).join("")}`, alpha };
}

/**
 * A single CSS box-shadow → `{ x, y, blur, spread, color, alpha, inset }`, or
 * null when this editor cannot round-trip it.
 */
export function parseBoxShadow(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw.toLowerCase() === "none") return null;

  // A top-level comma means more than one shadow. Editing the first would drop
  // the rest, so those stay text.
  let depth = 0;
  for (const char of raw) {
    if (char === "(") depth += 1;
    else if (char === ")") depth = Math.max(0, depth - 1);
    else if (char === "," && depth === 0) return null;
  }

  const tokens = splitShadowTokens(raw);
  const inset = tokens.some((token) => token.toLowerCase() === "inset");
  const rest = tokens.filter((token) => token.toLowerCase() !== "inset");

  const lengths = [];
  let color = null;

  for (const token of rest) {
    if (BOX_SHADOW_LENGTH_RE.test(token)) {
      lengths.push(Number(token.replace("px", "")));
      continue;
    }
    // The colour may sit at either end, but there is only ever one of it.
    if (color) return null;
    color = parseShadowColor(token);
    if (!color) return null;
  }

  // x and y are required; blur and spread are not.
  if (lengths.length < 2 || lengths.length > 4) return null;

  return {
    x: lengths[0],
    y: lengths[1],
    blur: lengths.length > 2 ? lengths[2] : 0,
    spread: lengths.length > 3 ? lengths[3] : 0,
    color: color ? color.color : "#000000",
    alpha: color ? color.alpha : 1,
    inset,
  };
}

/** The inverse. Always writes all four lengths, so what is read back is what was written. */
export function formatBoxShadow(shadow) {
  const num = (value) => {
    const parsed = Number(value);
    return isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
  };
  const alpha = Math.min(1, Math.max(0, isFinite(Number(shadow.alpha)) ? Number(shadow.alpha) : 1));
  const hex = /^#[0-9a-f]{6}$/i.test(shadow.color || "") ? shadow.color.toLowerCase() : "#000000";

  let color = hex;
  if (alpha < 1) {
    const channel = (index) => parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);
    color = `rgba(${channel(0)}, ${channel(1)}, ${channel(2)}, ${Math.round(alpha * 100) / 100})`;
  }

  return `${shadow.inset ? "inset " : ""}${num(shadow.x)}px ${num(shadow.y)}px ` +
    `${num(shadow.blur)}px ${num(shadow.spread)}px ${color}`;
}
